import { useAuth } from '@/contexts/auth-context';
import { useFeatureFlagGate, type FeatureGateStatus } from '@/hooks/use-feature-flag-gate';
import { FeatureFlag } from '@fluenceur/types';

/**
 * Who may read the gifting surface: a brand whose Shopify store is connected
 * (nothing is sent without one) and whose organization runs at least one
 * producer of gifts, Flows or Collabs. No dedicated feature flag exists, and
 * none is wanted: the gate describes a capability the brand already has.
 *
 * Pure so the branch logic is unit-testable without rendering, and so the nav
 * and the page can never disagree about the answer.
 *
 * `shopifyResolved` says whether the Shopify answer is final. It is read from
 * the session, which the protected layout has already loaded by the time any
 * gated surface renders, so the nav entry never waits on a request of its own.
 */
export const giftAccessDecision = (
  flows: FeatureGateStatus,
  collabs: FeatureGateStatus,
  shopify: { isConnected: boolean } | undefined,
  shopifyResolved: boolean
): FeatureGateStatus => {
  const producer: FeatureGateStatus =
    flows === 'granted' || collabs === 'granted'
      ? 'granted'
      : flows === 'denied' && collabs === 'denied'
        ? 'denied'
        : 'loading';

  if (producer === 'granted' && shopify?.isConnected === true) return 'granted';
  // A settled "no" on either side is decisive, whatever the other side is
  // still doing: neither answer can be overturned by the one still pending.
  if (producer === 'denied') return 'denied';
  if (shopifyResolved && shopify?.isConnected !== true) return 'denied';
  return 'loading';
};

/**
 * The gifting gate, read identically by the sidebar entry (anything but
 * `granted` hides it) and by the page (`loading` waits, `denied` redirects).
 *
 * The Shopify state comes from the session payload rather than from the
 * integrations query: that query refetches on every mount and retries on
 * failure, and the sidebar was hiding the entry for the whole round trip.
 */
export const useGiftingAccess = (): FeatureGateStatus => {
  const flows = useFeatureFlagGate(FeatureFlag.FLOWS);
  const collabs = useFeatureFlagGate(FeatureFlag.COLLABS);
  const { user } = useAuth();

  return giftAccessDecision(flows, collabs, user?.integrations?.shopify, user !== null);
};
